/**
 * Shared contract between the fill-template-modal (opener) and the fill-template-preview page
 * it opens in a new tab per generated document (#1231, Figma 5405:133176).
 *
 * The preview tab has no access to the opener's component state (separate JS context), so the
 * two communicate via:
 *  - sessionStorage: the opener writes a FillTemplatePreviewPayload under `${SESSION_STORAGE_KEY_PREFIX}${id}`
 *    before calling window.open() — sessionStorage written just before window.open() from the same
 *    document is cloned into the new tab per the HTML spec, so the preview page can read it back.
 *  - postMessage: the preview tab's "add to task/contract" action can only run in the opener (it may
 *    be a pure in-memory staging step with no backend id yet, e.g. an unsaved contract draft), so the
 *    tab asks the opener to perform it and waits for the reply.
 */

export const SESSION_STORAGE_KEY_PREFIX = 'fillTemplatePreview:';

export interface FillTemplatePreviewPayload {
  id: string;
  name: string;
  /** Blob URL, valid only while the opener tab stays open. */
  pdfUrl: string | null;
  /** Blob URL of the filled document, valid only while the opener tab stays open. */
  fileUrl: string;
  /** Extension the download is named with, '.docx' or '.xlsx' — matches the template's format. */
  fileExtension: string;
  /** null for the export context, where there is nothing to attach the document to. */
  primaryLabel: string | null;
}

export const FILL_TEMPLATE_PREVIEW_MESSAGE = {
  /** preview tab -> opener: please run addToContext for this doc. */
  Save: 'fillTemplatePreview:save',
  /** opener -> preview tab: addToContext finished. */
  Saved: 'fillTemplatePreview:saved',
} as const;

export interface FillTemplatePreviewSaveMessage {
  type: typeof FILL_TEMPLATE_PREVIEW_MESSAGE.Save;
  id: string;
  name: string;
}

export interface FillTemplatePreviewSavedMessage {
  type: typeof FILL_TEMPLATE_PREVIEW_MESSAGE.Saved;
  id: string;
  success: boolean;
}
